import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchInspectionSummary,
  type InspectionGroupSummary,
  type InspectionSummary as InspectionSummaryView,
} from '../api/inspections';
import { ca } from '../i18n/ca';
import { PageLoading } from './PageStates';
import { Shell } from './Shell';
import { errorText } from './useSessionLive';

/** One breakdown table: a group's own counts, one row per group. */
function GroupTable({ title, groups }: { title: string; groups: InspectionGroupSummary[] }) {
  const t = ca.inspectionSummaryPage;
  if (groups.length === 0) {
    return (
      <article className="card" aria-label={title}>
        <h2>{title}</h2>
        <p className="muted small">{t.noGroups}</p>
      </article>
    );
  }
  return (
    <article className="card" aria-label={title}>
      <h2>{title}</h2>
      <table className="summary-table">
        <thead>
          <tr>
            <th>{t.group}</th>
            <th className="num">{t.total}</th>
            <th className="num">{t.reviewed}</th>
            <th className="num">{t.flags.correct}</th>
            <th className="num">{t.flags.problem}</th>
            <th className="num">{t.flags.useless}</th>
            <th className="num">{t.flags.repeat}</th>
            <th className="num">{t.flags.mismatch}</th>
            <th className="num">{t.qc.empty}</th>
            <th className="num">{t.qc.dark}</th>
            <th className="num">{t.qc.duplicate}</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.key}>
              <td>{g.label}</td>
              <td className="num">{g.counts.total}</td>
              <td className="num">{g.counts.reviewed}</td>
              <td className="num">{g.counts.correct}</td>
              <td className="num">{g.counts.problem}</td>
              <td className="num">{g.counts.useless}</td>
              <td className="num">{g.counts.repeat}</td>
              <td className="num">{g.counts.mismatch}</td>
              <td className="num">{g.counts.qcEmpty}</td>
              <td className="num">{g.counts.qcDark}</td>
              <td className="num">{g.counts.qcDuplicate}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </article>
  );
}

/**
 * The general correction ({@code /proves/inspeccions/resum}): every review
 * and QC flag every campaign's own tours produced, aggregated by recipe,
 * objective type, stop kind and force role, plus every note a person left —
 * one page for a person or for Claude, instead of walking every session's own
 * review page (build item 3).
 */
export function InspectionSummary() {
  const t = ca.inspectionSummaryPage;
  const [summary, setSummary] = useState<InspectionSummaryView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchInspectionSummary()
      .then(setSummary)
      .catch((e) => setError(errorText(e)));
  }, []);

  return (
    <Shell title={t.title} width="wide">
      <p className="muted">{t.intro}</p>
      {!summary && !error && <PageLoading />}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {summary && (
        <>
          <p>{t.overall(summary.totalReviewed, summary.totalCaptures)}</p>
          <GroupTable title={t.byRecipe} groups={summary.byRecipe} />
          <GroupTable title={t.byObjectiveType} groups={summary.byObjectiveType} />
          <GroupTable title={t.byStopKind} groups={summary.byStopKind} />
          <GroupTable title={t.byForceRole} groups={summary.byForceRole} />
          <article className="card" aria-label={t.notes}>
            <h2>{t.notes}</h2>
            {summary.notes.length === 0 ? (
              <p className="muted small">{t.noNotes}</p>
            ) : (
              <ul className="card-list">
                {summary.notes.map((n) => (
                  <li key={n.uploadId} className="card">
                    <p>{n.note}</p>
                    <p className="muted small">
                      {n.label} · {n.recipeId} · {n.worldClass}
                    </p>
                    <p>
                      <Link to={`/proves/inspeccio/${n.sessionCode}`}>{t.openSession}</Link>
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </article>
        </>
      )}
    </Shell>
  );
}
